/*
  Fast Simon Shopper Activity Reporting for the autocomplete search dropdown.
  https://docs.fastsimon.com/api/api-doc.html#tag/Serving-API/operation/postLoadAC

  Exposes window.FastSimonTracking for fast-simon-search.js.liquid to call into.
  Kept in its own file/script tag (separate from the search dropdown logic) so an
  AB test can later swap which search implementation loads without touching this.
*/
(function () {
  const FAST_SIMON_UUID = '8ee3c5e8-6d27-4e3c-bae7-19638a81e5ac';
  const FAST_SIMON_STORE_ID = '9418836';
  const FAST_SIMON_HOST = window.location.hostname;

  const SHOPPER_TOKEN_KEY = 'fs_shopper_token';
  const SESSION_START_KEY = 'fs_session_start';
  const SESSION_LAST_ACTIVITY_KEY = 'fs_session_last_activity';
  const SESSION_IDLE_LIMIT_MS = 30 * 60 * 1000;

  let shopperTokenPromise = null;

  // Fast Simon issues a shopper token via /post_load (as `isp_token`) and
  // expects it resent as `st` on every reporting call. Cached in localStorage
  // so it survives reloads.
  function fetchFastSimonShopperToken() {
    if (shopperTokenPromise) return shopperTokenPromise;

    const stored = localStorage.getItem(SHOPPER_TOKEN_KEY);
    if (stored) {
      shopperTokenPromise = Promise.resolve(stored);
      return shopperTokenPromise;
    }

    const params = new URLSearchParams({ store_id: FAST_SIMON_STORE_ID, UUID: FAST_SIMON_UUID });
    shopperTokenPromise = fetch(`https://ping.fastsimon.com/post_load?${params}`)
      .then((response) => response.json())
      .then((data) => {
        const token = data.isp_token || '';
        if (!token) {
          console.warn('Fast Simon: could not find a shopper token in /post_load response', data);
        } else {
          localStorage.setItem(SHOPPER_TOKEN_KEY, token);
        }
        return token;
      })
      .catch((err) => {
        console.error('Fast Simon: failed to fetch shopper token', err);
        return '';
      });

    return shopperTokenPromise;
  }

  // Client-owned session boundary (independent of the shopper token's own
  // server-side association window). Restarts after 30 minutes of inactivity.
  function getFastSimonSessionStart() {
    const now = Date.now();
    const storedStart = Number(localStorage.getItem(SESSION_START_KEY));
    const lastActivity = Number(localStorage.getItem(SESSION_LAST_ACTIVITY_KEY));

    const isExpired = !storedStart || !lastActivity || now - lastActivity > SESSION_IDLE_LIMIT_MS;
    const sessionStart = isExpired ? now : storedStart;

    localStorage.setItem(SESSION_START_KEY, String(sessionStart));
    localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));

    return Math.floor(sessionStart / 1000);
  }

  async function postFastSimonAutocompleteEvent(params) {
    const st = await fetchFastSimonShopperToken();
    const session = getFastSimonSessionStart();

    const query = new URLSearchParams({
      store_id: FAST_SIMON_STORE_ID,
      UUID: FAST_SIMON_UUID,
      st,
      session,
      l: FAST_SIMON_HOST,
      ...params,
    });

    fetch(`https://ping.fastsimon.com/post_load_ac?${query}`).catch((err) => {
      console.error('Fast Simon: autocomplete tracking failed', err);
    });
  }

  // NOTE: Fast Simon's docs specify this event fires "5 seconds after a product
  // page is viewed from autocomplete" - i.e. from the product page itself, not
  // at click time. This fires at click time instead, which is a simplification:
  // it tells Fast Simon a product was selected, but not confirmed viewed. The
  // fuller version would need the product page template to pick this up via
  // sessionStorage and fire after a 5s delay - out of scope for this pass.
  function trackFastSimonProductSelected(term, productId, productVariantQuery) {
    postFastSimonAutocompleteEvent({
      prev_up_type: '3',
      original_search_query: term,
      id: productId,
      q: productVariantQuery,
    });
  }

  function trackFastSimonPopularSearchSelected(term, selectedTerm) {
    postFastSimonAutocompleteEvent({
      prev_up_type: '1',
      original_search_query: term,
      q: selectedTerm,
    });
  }

  window.FastSimonTracking = {
    trackProductSelected: trackFastSimonProductSelected,
    trackPopularSearchSelected: trackFastSimonPopularSearchSelected,
  };
})();
